// client/src/components/pages/WeighbridgeCompliance/CalibrationTracker.tsx (converted from .js, ST-00216)
import { ThemedSelect } from '../../../types/commonComponents';
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ChangeEvent,
  type ComponentType,
} from 'react';
import { Plus, Edit2, Trash2, ShieldCheck, Upload } from 'lucide-react';
import { toast } from 'react-toastify';
import { parseISO, differenceInDays } from 'date-fns';
import { formatAppDate } from '../../../utils/dateUtils';
import apiJs from '../../../services/weighbridgeComplianceApi';
import CalibrationModalJs from './CalibrationModal';
import type {
  CalibrationListParams,
  CalibrationRecord,
  ComplianceApiError,
  ComplianceAssetOption,
  PaginatedBody,
} from '../../../types/clientPages/weighbridgeCompliance';

// Typed views of the JS modules (weighbridgeComplianceApi is converted by another stream).
interface CalibrationTrackerApi {
  calibrations: {
    getAll: (
      params: CalibrationListParams
    ) => Promise<{ data: PaginatedBody<CalibrationRecord> | CalibrationRecord[] }>;
    delete: (id: number) => Promise<unknown>;
  };
  assets: {
    getAll: () => Promise<{
      data: PaginatedBody<ComplianceAssetOption> | ComplianceAssetOption[];
    }>;
  };
  documents: { upload: (formData: FormData) => Promise<unknown> };
}
const api = apiJs as unknown as CalibrationTrackerApi;
const CalibrationModal = CalibrationModalJs as unknown as ComponentType<{
  calibration: CalibrationRecord | null;
  assets: ComplianceAssetOption[];
  onClose: () => void;
  onSaved: () => void;
}>;

interface BadgeStyle {
  bg: string;
  text: string;
  border: string;
}

interface StatusStyle extends BadgeStyle {
  dot: string;
  label: string;
}

type CertificateStatus = 'valid' | 'expiring' | 'expired';

const CertTypeBadge = ({ type }: { type: string }) => {
  const configs: Record<string, BadgeStyle | undefined> & { Calibration: BadgeStyle } = {
    Calibration: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
    Verification: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
    Both: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  };
  const config = configs[type] || configs.Calibration;
  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold border px-2.5 py-1 text-xs ${config.bg} ${config.text} ${config.border}`}
    >
      {type}
    </span>
  );
};

const StatusBadge = ({ status }: { status: CertificateStatus }) => {
  const configs: Record<CertificateStatus, StatusStyle> = {
    valid: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      dot: 'bg-emerald-500',
      label: 'Valid',
    },
    expiring: {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      dot: 'bg-amber-500',
      label: 'Expiring Soon',
    },
    expired: {
      bg: 'bg-red-50',
      text: 'text-red-700',
      border: 'border-red-200',
      dot: 'bg-red-500',
      label: 'Expired',
    },
  };
  const config = configs[status] || configs.valid;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold border px-2.5 py-1 text-xs ${config.bg} ${config.text} ${config.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

const CalibrationTracker = () => {
  const [calibrations, setCalibrations] = useState<CalibrationRecord[]>([]);
  const [assets, setAssets] = useState<ComplianceAssetOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [assetFilter, setAssetFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [certTypeFilter, setCertTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editCalibration, setEditCalibration] = useState<CalibrationRecord | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const uploadInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: CalibrationListParams = {};
      if (assetFilter) params.assetId = assetFilter;
      if (statusFilter) params.status = statusFilter;
      if (certTypeFilter) params.type = certTypeFilter;

      const [calRes, assetsRes] = await Promise.all([
        api.calibrations.getAll(params),
        api.assets.getAll(),
      ]);
      setCalibrations(
        (calRes.data as PaginatedBody<CalibrationRecord>)?.data ||
          (calRes.data as CalibrationRecord[]) ||
          []
      );
      setAssets(
        (assetsRes.data as PaginatedBody<ComplianceAssetOption>)?.data ||
          (assetsRes.data as ComplianceAssetOption[]) ||
          []
      );
    } catch (err) {
      // One message per failing endpoint, however often the filters reload it (ST-00259).
      toast.error('Failed to load calibration data', { toastId: 'compliance-calibrations-load' });
    } finally {
      setLoading(false);
    }
  }, [assetFilter, statusFilter, certTypeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) return;
    setDeleting(id);
    try {
      await api.calibrations.delete(id);
      toast.success('Certificate deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete certificate');
    } finally {
      setDeleting(null);
    }
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditCalibration(null);
    fetchData();
  };

  const handleDocUpload = async (calibrationId: number, e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Same field names and entity type as the Calibration modal and the
      // Documents tab — the server requires linked_entity_type/linked_entity_id
      // and rejected the old entityType/entityId with a 400 (WBE-8).
      formData.append('linked_entity_type', 'Calibration');
      formData.append('linked_entity_id', String(calibrationId));
      formData.append('description', `Certificate: ${file.name}`);
      await api.documents.upload(formData);
      toast.success('Document uploaded');
    } catch (err) {
      toast.error((err as ComplianceApiError).response?.data?.error || 'Failed to upload document');
    }
    e.target.value = '';
  };

  const getDaysRemaining = (expiryDate: string | null | undefined) => {
    if (!expiryDate) return null;
    return differenceInDays(parseISO(expiryDate), new Date());
  };

  const getStatus = (expiryDate: string | null | undefined): CertificateStatus => {
    const days = getDaysRemaining(expiryDate);
    if (days === null) return 'expired';
    if (days <= 0) return 'expired';
    if (days <= 90) return 'expiring';
    return 'valid';
  };

  const getDaysColor = (days: number | null) => {
    if (days === null) return 'text-slate-400';
    if (days <= 0) return 'text-red-600 font-semibold';
    if (days <= 30) return 'text-red-500 font-medium';
    if (days <= 60) return 'text-amber-600 font-medium';
    if (days <= 90) return 'text-amber-500';
    return 'text-emerald-600';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            Calibration & Verification Tracker
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Track calibration and verification certificates for all assets
          </p>
        </div>
        <button
          onClick={() => {
            setEditCalibration(null);
            setShowModal(true);
          }}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-xs font-semibold flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> Add Certificate
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <ThemedSelect
            value={assetFilter}
            onChange={(e) => setAssetFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm text-slate-600"
          >
            <option value="">All Assets</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </ThemedSelect>
        </div>

        <div className="relative">
          <ThemedSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm text-slate-600"
          >
            <option value="">All Statuses</option>
            <option value="Valid">Valid</option>
            <option value="expiring">Expiring Soon</option>
            <option value="Expired">Expired</option>
          </ThemedSelect>
        </div>

        <div className="relative">
          <ThemedSelect
            value={certTypeFilter}
            onChange={(e) => setCertTypeFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm text-slate-600"
          >
            <option value="">All Types</option>
            <option value="Calibration">Calibration</option>
            <option value="Verification">Verification</option>
            <option value="Both">Both</option>
          </ThemedSelect>
        </div>
      </div>

      {loading ? (
        <div className="bg-surface rounded-xl border border-slate-200 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-1/3" />
            <div className="h-4 bg-slate-200 rounded w-full" />
            <div className="h-4 bg-slate-200 rounded w-full" />
            <div className="h-4 bg-slate-200 rounded w-3/4" />
            <div className="h-4 bg-slate-200 rounded w-full" />
          </div>
        </div>
      ) : calibrations.length === 0 ? (
        <div className="bg-surface rounded-xl border border-slate-200">
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center mb-4">
              <ShieldCheck className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">No certificates found</h3>
            <p className="text-sm text-slate-400 max-w-sm">
              {assetFilter || statusFilter || certTypeFilter
                ? 'Try adjusting your filter criteria'
                : 'Start by adding calibration or verification certificates'}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-100">
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Asset Name
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Cert Type
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Certificate No
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Issue Date
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Expiry Date
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Days Left
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Lab
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Status
                  </th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {calibrations.map((cal) => {
                  const days = getDaysRemaining(cal.next_due_date);
                  const status = getStatus(cal.next_due_date);
                  return (
                    <tr key={cal.id} className="hover:bg-primary-50/30 transition-colors">
                      <td className="px-5 py-3.5 text-sm text-slate-700 font-medium">
                        {cal.asset_name || '-'}
                      </td>
                      <td className="px-5 py-3.5">
                        <CertTypeBadge type={cal.calibration_type} />
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-600 font-mono text-xs">
                        {cal.certificate_number || '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">
                        {cal.issue_date ? formatAppDate(cal.issue_date) : '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">
                        {cal.next_due_date ? formatAppDate(cal.next_due_date) : '-'}
                      </td>
                      <td className={`px-5 py-3.5 text-sm ${getDaysColor(days)}`}>
                        {days !== null
                          ? days <= 0
                            ? `${Math.abs(days)}d overdue`
                            : `${days}d`
                          : '-'}
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">
                        {cal.calibrating_entity || '-'}
                      </td>
                      <td className="px-5 py-3.5">
                        <StatusBadge status={status} />
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditCalibration(cal);
                              setShowModal(true);
                            }}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              const ref = uploadInputRefs.current[cal.id];
                              if (ref) ref.click();
                            }}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                            title="Upload document"
                          >
                            <Upload className="w-4 h-4" />
                          </button>
                          <input
                            ref={(el) => {
                              uploadInputRefs.current[cal.id] = el;
                            }}
                            type="file"
                            accept=".pdf"
                            aria-label={`Upload document for certificate ${cal.certificate_number || cal.id}`}
                            onChange={(e) => handleDocUpload(cal.id, e)}
                            className="hidden"
                          />
                          <button
                            onClick={() => handleDelete(cal.id)}
                            disabled={deleting === cal.id}
                            className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <CalibrationModal
          calibration={editCalibration}
          assets={assets}
          onClose={() => {
            setShowModal(false);
            setEditCalibration(null);
          }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default CalibrationTracker;
